/**
 * Legacy to Mosaic layout conversion.
 *
 * When a user enables mosaic mode, their existing widget layouts may have:
 * - Positions/sizes not aligned to the 2% mosaic grid
 * - Overlapping widgets
 * - Widgets smaller than the minimum size (6%)
 *
 * This module provides functions to convert legacy layouts to valid mosaic layouts
 * while preserving the original layout as much as possible.
 */
import { MIN_SIZE, MOSAIC_GRID_INCREMENT, snapToMosaicGrid, snapLayoutToMosaicGrid } from './mosaic-drop-targets';
import type { NormalizedLayout } from './mosaic-drop-targets';
import { findOptimalWidgetPlacement } from './mosaic-widget-placement';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'mosaic-legacy-conversion' });

/**
 * Validation result for a mosaic layout.
 */
export interface ValidationResult {
    valid: boolean;
    issues: string[];
}

/**
 * Check if two layouts overlap.
 * Uses a small epsilon to avoid floating point comparison issues.
 */
export function rectanglesOverlap(a: NormalizedLayout, b: NormalizedLayout): boolean {
    const epsilon = 0.0001;
    // Two rectangles do NOT overlap if one is entirely to the left, right, above, or below the other
    const noOverlap =
        a.x + a.w <= b.x + epsilon || // a is left of b
        b.x + b.w <= a.x + epsilon || // b is left of a
        a.y + a.h <= b.y + epsilon || // a is above b
        b.y + b.h <= a.y + epsilon; // b is above a

    return !noOverlap;
}

/**
 * Normalize a single widget layout to mosaic constraints.
 * - Snaps position and size to 2% grid
 * - Enforces minimum 6% size
 * - Clamps to viewport bounds [0, 1]
 */
export function normalizeWidget(layout: NormalizedLayout): NormalizedLayout {
    // Step 1: Snap to grid
    let { x, y, w, h } = snapLayoutToMosaicGrid(layout);

    // Step 2: Enforce minimum size
    w = Math.max(MIN_SIZE, w);
    h = Math.max(MIN_SIZE, h);

    // Step 3: Clamp to viewport bounds
    // If widget extends beyond right edge, shift left
    if (x + w > 1) {
        x = Math.max(0, snapToMosaicGrid(1 - w));
    }
    // If widget extends beyond bottom edge, shift up
    if (y + h > 1) {
        y = Math.max(0, snapToMosaicGrid(1 - h));
    }
    // Clamp position to non-negative
    x = Math.max(0, x);
    y = Math.max(0, y);

    return { x, y, w, h };
}

/**
 * Check if a position is valid (doesn't overlap with placed widgets).
 */
function isPositionValid(layout: NormalizedLayout, placed: Record<string, NormalizedLayout>): boolean {
    for (const placedLayout of Object.values(placed)) {
        if (rectanglesOverlap(layout, placedLayout)) {
            return false;
        }
    }
    return true;
}

/**
 * Try to find a nearby non-overlapping position using spiral search.
 * Returns null if no valid position found within maxRadius.
 */
export function findNearbyNonOverlappingPosition(
    widget: NormalizedLayout,
    placed: Record<string, NormalizedLayout>,
    maxRadius = 10,
): NormalizedLayout | null {
    const { w, h } = widget;
    const originalX = widget.x;
    const originalY = widget.y;

    // Spiral search in Manhattan distance rings
    for (let radius = 1; radius <= maxRadius; radius++) {
        // Generate all positions at this Manhattan distance
        for (let dx = -radius; dx <= radius; dx++) {
            for (let dy = -radius; dy <= radius; dy++) {
                // Only consider positions exactly at this radius (perimeter of diamond)
                if (Math.abs(dx) + Math.abs(dy) !== radius) continue;

                const x = snapToMosaicGrid(originalX + dx * MOSAIC_GRID_INCREMENT);
                const y = snapToMosaicGrid(originalY + dy * MOSAIC_GRID_INCREMENT);

                // Skip if outside viewport
                if (x < 0 || y < 0 || x + w > 1 + 0.0001 || y + h > 1 + 0.0001) continue;

                const candidate: NormalizedLayout = { x, y, w, h };
                if (isPositionValid(candidate, placed)) {
                    return candidate;
                }
            }
        }
    }

    return null;
}

/**
 * Resolve overlaps in a set of normalized layouts.
 * Uses priority by area (larger widgets placed first).
 * Falls back to findOptimalWidgetPlacement when spiral search fails.
 */
export function resolveOverlaps(layouts: Record<string, NormalizedLayout>): Record<string, NormalizedLayout> {
    const ids = Object.keys(layouts);
    if (ids.length === 0) return {};

    // Sort by area (largest first) - larger widgets get priority
    const sortedIds = [...ids].sort((a, b) => {
        const areaA = layouts[a].w * layouts[a].h;
        const areaB = layouts[b].w * layouts[b].h;
        return areaB - areaA;
    });

    const result: Record<string, NormalizedLayout> = {};

    for (const widgetId of sortedIds) {
        const widget = layouts[widgetId];

        // Check if current position overlaps with any already-placed widget
        if (isPositionValid(widget, result)) {
            // No overlap - keep position
            result[widgetId] = widget;
            continue;
        }

        // Try spiral search for nearby position
        const nearbyPosition = findNearbyNonOverlappingPosition(widget, result);
        if (nearbyPosition) {
            result[widgetId] = nearbyPosition;
            if (TRACE) {
                log.trace({
                    message: 'resolveOverlaps: spiral search found position',
                    widgetId,
                    original: widget,
                    newPosition: nearbyPosition,
                });
            }
            continue;
        }

        // Spiral search failed - use findOptimalWidgetPlacement (same as "add widget")
        const placement = findOptimalWidgetPlacement(result, { w: widget.w, h: widget.h });
        if (placement) {
            result[widgetId] = placement.layout;

            // Apply any affected layouts from the placement
            if (placement.affectedLayouts) {
                for (const [affectedId, affectedLayout] of Object.entries(placement.affectedLayouts)) {
                    result[affectedId] = affectedLayout;
                }
            }

            if (TRACE) {
                log.trace({
                    message: 'resolveOverlaps: used findOptimalWidgetPlacement',
                    widgetId,
                    original: widget,
                    placement,
                });
            }
        } else {
            // This should be extremely rare - log a warning and keep original position
            log.warn({
                message: 'resolveOverlaps: no valid placement found, keeping original',
                widgetId,
                original: widget,
            });
            result[widgetId] = widget;
        }
    }

    return result;
}

/**
 * Check if a value is grid-aligned (multiple of MOSAIC_GRID_INCREMENT).
 */
function isGridAligned(value: number): boolean {
    const remainder = value % MOSAIC_GRID_INCREMENT;
    return remainder < 0.001 || remainder > MOSAIC_GRID_INCREMENT - 0.001;
}

/**
 * Validate that a layout satisfies all mosaic constraints.
 */
export function validateMosaicLayout(layouts: Record<string, NormalizedLayout>): ValidationResult {
    const issues: string[] = [];
    const ids = Object.keys(layouts);

    for (const id of ids) {
        const layout = layouts[id];

        // Check grid alignment
        if (!isGridAligned(layout.x)) {
            issues.push(`${id}.x (${layout.x}) not grid-aligned`);
        }
        if (!isGridAligned(layout.y)) {
            issues.push(`${id}.y (${layout.y}) not grid-aligned`);
        }
        if (!isGridAligned(layout.w)) {
            issues.push(`${id}.w (${layout.w}) not grid-aligned`);
        }
        if (!isGridAligned(layout.h)) {
            issues.push(`${id}.h (${layout.h}) not grid-aligned`);
        }

        // Check minimum size
        if (layout.w < MIN_SIZE - 0.001) {
            issues.push(`${id}.w (${layout.w}) below minimum ${MIN_SIZE}`);
        }
        if (layout.h < MIN_SIZE - 0.001) {
            issues.push(`${id}.h (${layout.h}) below minimum ${MIN_SIZE}`);
        }

        // Check viewport bounds
        if (layout.x < -0.001 || layout.x + layout.w > 1.001) {
            issues.push(`${id} exceeds horizontal bounds (x=${layout.x}, w=${layout.w})`);
        }
        if (layout.y < -0.001 || layout.y + layout.h > 1.001) {
            issues.push(`${id} exceeds vertical bounds (y=${layout.y}, h=${layout.h})`);
        }
    }

    // Check for overlaps
    for (let i = 0; i < ids.length - 1; i++) {
        for (let j = i + 1; j < ids.length; j++) {
            if (rectanglesOverlap(layouts[ids[i]], layouts[ids[j]])) {
                issues.push(`${ids[i]} overlaps with ${ids[j]}`);
            }
        }
    }

    return {
        valid: issues.length === 0,
        issues,
    };
}

/**
 * Convert legacy layouts to mosaic-compatible layouts.
 *
 * This is the main entry point for layout conversion. It:
 * 1. Normalizes each widget (snap to grid, enforce min size, clamp bounds)
 * 2. Resolves overlaps (priority by area, spiral search, then findOptimalWidgetPlacement)
 * 3. Validates the result
 *
 * @param legacyLayouts - The original layouts keyed by container ID
 * @returns Converted layouts that satisfy mosaic constraints
 */
export function convertLegacyToMosaic(
    legacyLayouts: Record<string, NormalizedLayout>,
): Record<string, NormalizedLayout> {
    const ids = Object.keys(legacyLayouts);
    if (ids.length === 0) {
        return {};
    }

    if (TRACE) {
        log.trace({
            message: 'convertLegacyToMosaic: starting conversion',
            widgetCount: ids.length,
            layouts: legacyLayouts,
        });
    }

    // Phase 1: Normalize each widget individually
    const normalizedLayouts: Record<string, NormalizedLayout> = {};
    for (const id of ids) {
        normalizedLayouts[id] = normalizeWidget(legacyLayouts[id]);
    }

    if (TRACE) {
        log.trace({
            message: 'convertLegacyToMosaic: after normalization',
            layouts: normalizedLayouts,
        });
    }

    // Phase 2: Resolve overlaps
    const resolvedLayouts = resolveOverlaps(normalizedLayouts);

    if (TRACE) {
        log.trace({
            message: 'convertLegacyToMosaic: after overlap resolution',
            layouts: resolvedLayouts,
        });
    }

    // Phase 3: Validate
    const validation = validateMosaicLayout(resolvedLayouts);
    if (!validation.valid) {
        log.warn({
            message: 'convertLegacyToMosaic: validation failed after conversion',
            issues: validation.issues,
        });
    }

    return resolvedLayouts;
}
